import React, { useState } from 'react';
import { FaLeaf, FaSun, FaWater, FaTint, FaFlask, FaSeedling, FaTree, FaBug, FaExchangeAlt, FaCloudRain, FaMountain, FaSpa } from 'react-icons/fa';
import PlotItem from './PlotItem';
import MetricCard from './MetricCard';
import MiniChart from './MiniChart';

const Sidebar = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const plots = [
    {
      id: 1,
      name: 'Montgomery Farm',
      location: 'Maryland, DMV Region',
      icon: <FaLeaf />,
      value: '68.1°',
      chartColor: 'var(--accent-green)',
      metricType: 'temperature',
      rotationStatus: 'Optimal',
      currentCrop: 'Corn',
      nextRecommendedCrop: 'Soybeans',
      biodiversityIndex: 78
    },
    {
      id: 2,
      name: 'Fairfax Acres',
      location: 'Virginia, DMV Region',
      icon: <FaSun />,
      value: '71.3°',
      chartColor: 'var(--accent-yellow)',
      metricType: 'temperature',
      rotationStatus: 'Needs Rotation',
      currentCrop: 'Wheat',
      nextRecommendedCrop: 'Legumes',
      biodiversityIndex: 65
    }
  ];

  const metrics = [
    {
      id: 1,
      name: 'Soil',
      label: 'Clay Loam Type',
      value: '16.7°',
      icon: <FaMountain />,
      chartColor: 'var(--accent-yellow)',
      metricType: 'soil',
      description: 'Soil temperature affects microbial activity'
    },
    {
      id: 2,
      name: 'Water',
      label: 'Normal Level',
      value: '92.2%',
      icon: <FaWater />,
      chartColor: 'var(--accent-blue)',
      metricType: 'water',
      description: 'Water availability for crops'
    },
    {
      id: 3,
      name: 'Volume of Water / Plant',
      label: 'Normal Level',
      value: '74.5%',
      icon: <FaTint />,
      chartColor: 'var(--accent-blue)',
      metricType: 'waterEfficiency',
      description: 'Water usage efficiency per plant'
    },
    {
      id: 4,
      name: 'Nutrient',
      label: 'Soon',
      value: '28.1%',
      icon: <FaFlask />,
      chartColor: 'var(--accent-green)',
      metricType: 'nutrient',
      description: 'Nutrient levels in soil'
    }
  ];

  const sustainabilityMetrics = [
    {
      id: 5,
      name: 'Biodiversity Index',
      label: 'Above Average',
      value: '76.3%',
      icon: <FaTree />,
      chartColor: 'var(--accent-green)',
      metricType: 'biodiversity',
      description: 'Measure of species diversity in the area'
    },
    {
      id: 6,
      name: 'Soil Health',
      label: 'Improving',
      value: '68.9%',
      icon: <FaSpa />,
      chartColor: 'var(--accent-yellow)',
      metricType: 'soilHealth',
      description: 'Overall soil health and fertility'
    },
    {
      id: 7,
      name: 'Crop Rotation Status',
      label: 'On Schedule',
      value: '91.2%',
      icon: <FaExchangeAlt />,
      chartColor: 'var(--accent-green)',
      metricType: 'cropRotation',
      description: 'Status of crop rotation implementation'
    },
    {
      id: 8,
      name: 'Pest Resistance',
      label: 'Strong',
      value: '82.5%',
      icon: <FaBug />,
      chartColor: 'var(--accent-green)',
      metricType: 'pestResistance',
      description: 'Natural resistance to pests and diseases'
    }
  ];

  const climateMetrics = [
    {
      id: 9,
      name: 'Carbon Sequestration',
      label: 'Increasing',
      value: '54.7%',
      icon: <FaMountain />,
      chartColor: 'var(--accent-green)',
      metricType: 'carbonSequestration',
      description: 'Carbon captured and stored in soil'
    },
    {
      id: 10,
      name: 'Precipitation',
      label: 'Seasonal Average',
      value: '19°',
      icon: <FaCloudRain />,
      chartColor: 'var(--accent-blue)',
      metricType: 'precipitation',
      description: 'Recent precipitation levels'
    },
    {
      id: 11,
      name: 'Yield Estimation',
      label: 'Above Target',
      value: '91%',
      icon: <FaSeedling />,
      chartColor: 'var(--accent-green)',
      metricType: 'yield',
      description: 'Projected crop yield'
    }
  ];

  const areaMetrics = [
    {
      id: 1,
      name: 'Prince George\'s County',
      value: '95',
      health: 'good',
      chartColor: 'var(--accent-green)',
      metricType: 'biodiversity',
      sustainabilityScore: 'A+',
      rotationCompliance: '98%'
    },
    {
      id: 2,
      name: 'Loudoun County',
      value: '84',
      health: 'warning',
      chartColor: 'var(--accent-yellow)',
      metricType: 'biodiversity',
      sustainabilityScore: 'B',
      rotationCompliance: '87%'
    }
  ];

  const valleyPlots = [
    {
      id: 3,
      name: 'Howard Valley',
      location: 'Maryland, DMV Region',
      icon: <FaLeaf />,
      value: '74.2°',
      chartColor: 'var(--accent-green)',
      metricType: 'temperature',
      rotationStatus: 'Optimal',
      currentCrop: 'Soybeans',
      nextRecommendedCrop: 'Winter Wheat',
      biodiversityIndex: 82
    },
    {
      id: 4,
      name: 'Arlington Fields',
      location: 'Virginia, DMV Region',
      icon: <FaLeaf />,
      value: '57.9°',
      chartColor: 'var(--accent-red)',
      metricType: 'temperature',
      rotationStatus: 'Critical',
      currentCrop: 'Corn',
      nextRecommendedCrop: 'Cover Crops',
      biodiversityIndex: 59
    }
  ];

  return (
    <div className="sidebar">
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('farms')}>
          <h3>Monitored Farms</h3>
          <span>{expandedSection === 'farms' ? '−' : '+'}</span>
        </div>
        {(expandedSection === 'farms' || expandedSection === null) && 
          plots.map(plot => (
            <PlotItem key={plot.id} plot={plot} />
          ))
        }
      </div>
      
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('environmental')}>
          <h3>Environmental Metrics</h3>
          <span>{expandedSection === 'environmental' ? '−' : '+'}</span>
        </div>
        {(expandedSection === 'environmental' || expandedSection === null) && 
          metrics.map(metric => (
            <MetricCard key={metric.id} metric={metric} />
          ))
        }
      </div>
      
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('sustainability')}>
          <h3>Sustainability Indicators</h3>
          <span>{expandedSection === 'sustainability' ? '−' : '+'}</span>
        </div>
        {expandedSection === 'sustainability' && 
          sustainabilityMetrics.map(metric => (
            <MetricCard key={metric.id} metric={metric} />
          ))
        }
      </div>
      
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('climate')}>
          <h3>Climate & Yield</h3>
          <span>{expandedSection === 'climate' ? '−' : '+'}</span>
        </div>
        {expandedSection === 'climate' && 
          climateMetrics.map(metric => (
            <MetricCard key={metric.id} metric={metric} />
          ))
        }
      </div>
      
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('regional')}>
          <h3>Regional Overview</h3>
          <span>{expandedSection === 'regional' ? '−' : '+'}</span>
        </div>
        {expandedSection === 'regional' && 
          areaMetrics.map(area => (
            <div className="plot-item" key={area.id}>
              <div className="plot-header">
                <div>
                  <div className="plot-name">
                    <span className={`health-indicator health-${area.health}`}></span>
                    {area.name}
                  </div>
                  <div className="plot-location">
                    Sustainability Score: {area.sustainabilityScore} | Rotation Compliance: {area.rotationCompliance}
                  </div>
                </div>
                <div className="metric-value">{area.value}</div>
              </div>
              <div className="chart-container">
                <MiniChart 
                  color={area.chartColor} 
                  metricType={area.metricType}
                  useDMVData={true}
                  height={50}
                />
              </div>
            </div>
          ))
        }
      </div>
      
      <div className="sidebar-section">
        <div className="section-header" onClick={() => toggleSection('additional')}>
          <h3>Additional Farms</h3>
          <span>{expandedSection === 'additional' ? '−' : '+'}</span>
        </div>
        {expandedSection === 'additional' && 
          valleyPlots.map(plot => (
            <PlotItem key={plot.id} plot={plot} />
          ))
        }
      </div>
      
      <div style={{ height: '50px' }}></div>
    </div>
  );
};

export default Sidebar; 